import { useState } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/shared/api/api-error';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';

import { useDecideApproval } from '../api/pricing.queries';
import type { Approval } from '../types/pricing.types';

/**
 * Approve or reject a pricing request (pricing.config.approve). A rejection
 * needs a reason, shown to the requester. Mount only while open.
 */
export function ApprovalDecisionDialog({
  approval,
  decision,
  onClose,
}: {
  approval: Approval;
  decision: 'approve' | 'reject';
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const decide = useDecideApproval();
  const rejecting = decision === 'reject';

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={rejecting ? 'Reject this request?' : 'Approve and make it live?'}
      description={
        rejecting
          ? `${approval.summary}. The draft stays as it is, and the requester can change and resubmit it.`
          : `${approval.summary}. Customers are priced with it from now on, in this country.`
      }
      confirmLabel={rejecting ? 'Reject' : 'Approve'}
      variant={rejecting ? 'destructive' : 'default'}
      isPending={decide.isPending}
      confirmDisabled={rejecting && reason.trim().length < 3}
      onConfirm={() =>
        decide.mutate(
          {
            approvalId: approval.id,
            decision,
            reason: reason.trim() === '' ? null : reason.trim(),
          },
          { onSuccess: onClose },
        )
      }
    >
      <div className="space-y-1.5">
        <Label htmlFor="approval-reason" className="text-xs">
          Reason{' '}
          <span className="text-muted-foreground font-normal">
            ({rejecting ? 'required' : 'optional'})
          </span>
        </Label>
        <Input
          id="approval-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          maxLength={500}
          className="h-8 text-xs"
        />
        {decide.error instanceof ApiError && (
          <p role="alert" className="text-destructive text-xs">
            {decide.error.message}
          </p>
        )}
      </div>
    </ConfirmDialog>
  );
}
